import { AuthProvider, useAuth } from './contexts/AuthContext'
import { GameProvider } from './contexts/GameContext'
import { WebSocketProvider } from './contexts/WebSocketContext'
import { TournamentProvider } from './contexts/TournamentContext'
import { NotificationProvider, useNotifications } from './contexts/NotificationContext'
import AuthPage from './components/Auth/AuthPage'
import Dashboard from './components/Dashboard'
import ResetPassword from './components/Auth/ResetPassword'
import VerifyEmail from './components/Auth/VerifyEmail'
import ToastContainer from './components/Toast/Toast'
import Toaster from './components/Toaster/Toaster'
import './App.css'

function NotificationToaster() {
  const { 
    notifications, 
    acceptChallenge, 
    declineChallenge, 
    acceptTournamentInvite,
    declineTournamentInvite,
    removeNotification 
  } = useNotifications()

  const handleAccept = (notification) => {
    if (notification.type === 'tournament') {
      acceptTournamentInvite(notification)
    } else {
      acceptChallenge(notification)
    }
  }

  const handleDecline = (notification) => {
    if (notification.type === 'tournament') {
      declineTournamentInvite(notification)
    } else {
      declineChallenge(notification)
    }
  }

  return (
    <Toaster
      notifications={notifications}
      onAccept={handleAccept}
      onDecline={handleDecline}
      onDismiss={removeNotification}
    />
  )
}

function AppContent() {
  const { user, loading } = useAuth()

  // Check if we're on the reset password page
  const isResetPasswordPage = window.location.pathname === '/reset-password'

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    )
  }

  if (isResetPasswordPage) {
    return <ResetPassword />
  }

  if (window.location.pathname === '/verify-email') {
    return <VerifyEmail />
  }

  if (!user) {
    return <AuthPage />
  }

  return (
    <>
      <WebSocketProvider>
        <NotificationProvider>
          <GameProvider>
            <TournamentProvider>
              <Dashboard />
              <NotificationToaster />
            </TournamentProvider>
          </GameProvider>
        </NotificationProvider>
      </WebSocketProvider>
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo-section">
              <img src="/logo512.png" alt="RiftChess Logo" className="footer-logo" />
              <div className="footer-text-content">
                <span className="footer-title">RiftChess</span>
                <span className="footer-subtitle">The ultimate chess experience</span>
                <div className="footer-social-icons">
                  <a href="https://x.com/riftchess" target="_blank" rel="noopener noreferrer" className="footer-social-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  <a href="https://twitch.tv/riftchessgame" target="_blank" rel="noopener noreferrer" className="footer-social-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                    </svg>
                  </a>
                  <a href="https://instagram.com/riftchess" target="_blank" rel="noopener noreferrer" className="footer-social-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a href="https://discord.gg/riftchess" target="_blank" rel="noopener noreferrer" className="footer-social-icon">
                    <svg viewBox="0 0 71 55" fill="currentColor">
                      <g clipPath="url(#clip0)">
                        <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                      </g>
                      <defs>
                        <clipPath id="clip0">
                          <rect width="71" height="55" fill="white"/>
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="footer-section footer-center">
            <div className="footer-center-links">
              <a href="mailto:contact@riftchess.com" className="footer-center-link">Support</a>
              <a href="/privacy" className="footer-center-link">Privacy Policy</a>
              <a href="/fairplay" className="footer-center-link">Cheating & Fair Play</a>
            </div>
          </div>
          <div className="footer-section">
            <a href="mailto:contact@riftchess.com" className="footer-contact">contact@riftchess.com</a>
            <span className="footer-text">© Copyright 2026 RiftChess.</span>
          </div>
        </div>
      </footer>
    </>
  )
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <AppContent />
        <ToastContainer />
      </div>
    </AuthProvider>
  )
}

export default App